// Workspace (simple-mode S3 3.2, D33) over the existing gateway routes:
//
//   GET    /workspaces/{id}             any member
//   PATCH  /workspaces/{id}  {name}     owner/admin (a ROLE check: workspaces.py update_workspace)
//   POST   /workspaces/{id}/transfer    owner only  (a ROLE check: workspaces.py transfer_ownership);
//                                      the target must be a current member (D39, 422 otherwise)
//   DELETE /workspaces/{id}             delete_team
//
// Rename sends `{name}` and nothing else. Transfer picks from current members only, then
// re-reads the session (the caller becomes admin). Delete needs the workspace's name typed
// exactly, then moves the session to the next workspace in the switcher's list.
//
// Plan (S4): the active workspace's plan, credits and counted limits from
// GET /entitlements, with the link to Plans.
// Developer settings (S4, shown only when Developer mode is effective): connect-link
// redirect origins (RedirectOriginsSection, manage_auth_configs) and the allowlist mode's
// current state (read-only here; it is switched in Settings → Toolkit rules).
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { MembersResponse, Workspace, WorkspacesResponse } from '../api/types'
import { useSession } from '../context/SessionContext'
import { isOwnerRole, useCan, useIsRole } from '../rbac'
import { ConfirmAction } from '../components/ConfirmAction'
import { Modal } from '../components/Modal'
import { memberName } from '../components/format'
import { PageHeader, QueryState } from '../components/ui'
import { useMode } from '../context/ModeContext'
import { useEntitlements } from '../entitlements'
import { PlanSummary } from '../entitlements/PlanSummary'
import { UpsellLink } from '../entitlements/PlanNote'
import { RedirectOriginsSection } from './RedirectOriginsSection'

function RenameSection({ workspace }: { workspace: Workspace }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const [name, setName] = useState(workspace.name)
  const rename = useMutation({
    mutationFn: (next: string) =>
      apiFetch<Workspace>(`/workspaces/${workspaceId}`, { method: 'PATCH', body: JSON.stringify({ name: next }) }),
    onSuccess: (ws, next) => {
      qc.setQueryData<Workspace>(qk.workspace(workspaceId), (old) => (old ? { ...old, name: ws?.name ?? next } : old))
      qc.setQueryData<WorkspacesResponse>(qk.workspaces, (old) =>
        old ? { ...old, workspaces: (old.workspaces ?? []).map((w) => (w.id === workspaceId ? { ...w, name: ws?.name ?? next } : w)) } : old,
      )
    },
  })
  const trimmed = name.trim()
  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!trimmed || trimmed === workspace.name || rename.isPending) return
    rename.mutate(trimmed)
  }
  return (
    <section className="card-section" aria-labelledby="ws-rename-title">
      <h2 id="ws-rename-title" className="section-title">Name</h2>
      <form className="inline-form" aria-label="Rename workspace" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="ws-name">Workspace name</label>
          <input
            id="ws-name"
            value={name}
            maxLength={120}
            onChange={(e) => {
              rename.reset()
              setName(e.target.value)
            }}
          />
        </div>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={!trimmed || trimmed === workspace.name || rename.isPending}
          aria-busy={rename.isPending}
        >
          {rename.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>
      {rename.isSuccess && <p className="muted" role="status">Saved.</p>}
      {rename.error ? <p className="form-error" role="alert">{errorMessage(rename.error)}</p> : null}
    </section>
  )
}

function TransferSection() {
  const { workspaceId, recheck } = useSession()
  const qc = useQueryClient()
  const members = useQuery({
    queryKey: qk.members(workspaceId),
    queryFn: () => apiFetch<MembersResponse>(`/workspaces/${workspaceId}/members`),
    enabled: !!workspaceId,
  })
  // D39: only a current member can receive ownership, and never the owner themself.
  const candidates = (members.data?.members ?? []).filter((m) => !isOwnerRole(m.role))
  const [target, setTarget] = useState('')
  const [confirming, setConfirming] = useState(false)
  const chosen = candidates.find((m) => m.user_id === target)
  const transfer = useMutation({
    mutationFn: (newOwnerId: string) =>
      apiFetch<Workspace>(`/workspaces/${workspaceId}/transfer`, {
        method: 'POST',
        body: JSON.stringify({ new_owner_id: newOwnerId }),
      }),
    onSuccess: () => {
      setConfirming(false)
      // The caller is now an admin: every role-shaped cache entry and the session are stale.
      qc.clear()
      recheck()
    },
  })

  return (
    <section className="card-section" aria-labelledby="ws-transfer-title">
      <h2 id="ws-transfer-title" className="section-title">Transfer ownership</h2>
      <p className="muted">
        The new owner must already be a member. You stay in the workspace as an admin.
      </p>
      <QueryState isLoading={members.isLoading} error={members.error} what="this workspace’s members" onRetry={() => members.refetch()}>
        {candidates.length === 0 ? (
          <p className="muted" role="note">Invite someone from Team first: ownership can only go to a current member.</p>
        ) : (
          <div className="inline-form">
            <div className="field">
              <label htmlFor="ws-transfer-to">New owner</label>
              <select id="ws-transfer-to" value={target} onChange={(e) => setTarget(e.target.value)}>
                <option value="">Choose a member…</option>
                {candidates.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {memberName(m)}{m.email && memberName(m) !== m.email ? ` (${m.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn"
              disabled={!chosen}
              onClick={() => {
                transfer.reset()
                setConfirming(true)
              }}
            >
              Transfer…
            </button>
          </div>
        )}
      </QueryState>
      {confirming && chosen && (
        <ConfirmAction
          title="Transfer ownership"
          confirmLabel="Transfer ownership"
          pendingLabel="Transferring…"
          pending={transfer.isPending}
          error={transfer.error ? errorMessage(transfer.error) : undefined}
          onConfirm={() => transfer.mutate(chosen.user_id)}
          onClose={() => setConfirming(false)}
        >
          <p>
            <strong>{memberName(chosen)}</strong> becomes the owner of this workspace. You become an admin and can’t
            undo this yourself.
          </p>
        </ConfirmAction>
      )}
    </section>
  )
}

function DeleteDialog({ workspace, onClose }: { workspace: Workspace; onClose: () => void }) {
  const { switchWorkspace, recheck } = useSession()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [typed, setTyped] = useState('')
  const matches = typed === workspace.name
  const del = useMutation({
    mutationFn: () => apiFetch(`/workspaces/${workspace.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      // Back to the switcher's next workspace: the first other one in GET /workspaces.
      const list = qc.getQueryData<WorkspacesResponse>(qk.workspaces)?.workspaces ?? []
      const next = list.find((w) => w.id !== workspace.id)
      navigate('/apps', { replace: true })
      if (next) {
        try {
          await switchWorkspace(next.id)
          return
        } catch {
          // Fall through: the gateway picks a workspace for the session on its own.
        }
      }
      qc.clear()
      recheck()
    },
  })
  return (
    <Modal
      title="Delete workspace"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={!matches || del.isPending}
            aria-busy={del.isPending}
            onClick={() => matches && !del.isPending && del.mutate()}
          >
            {del.isPending ? 'Deleting…' : 'Delete workspace'}
          </button>
        </>
      }
    >
      <p>
        This removes <strong>{workspace.name}</strong> and every member’s access to it. Connected apps, keys and
        triggers in it stop working.
      </p>
      <div className="field">
        <label htmlFor="ws-delete-confirm">Type <strong>{workspace.name}</strong> to confirm</label>
        <input id="ws-delete-confirm" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
      </div>
      {del.error ? <p className="form-error" role="alert">{errorMessage(del.error)}</p> : null}
    </Modal>
  )
}

function PlanSection() {
  const q = useEntitlements()
  return (
    <section className="card-section" aria-labelledby="ws-plan-title">
      <h2 id="ws-plan-title" className="section-title">Plan</h2>
      <QueryState isLoading={q.isLoading} error={q.error} what="this workspace’s plan" onRetry={() => q.refetch()}>
        {q.data && <PlanSummary ent={q.data} />}
      </QueryState>
      <p>
        <UpsellLink>See what each plan includes</UpsellLink>
      </p>
    </section>
  )
}

function DeveloperSettings({ workspace }: { workspace: Workspace }) {
  return (
    <>
      <section className="card-section" aria-labelledby="ws-allowlist-title">
        <h2 id="ws-allowlist-title" className="section-title">Allowlist mode</h2>
        <p data-testid="allowlist-state">
          {workspace.allowlist_mode
            ? 'On: only toolkits with an allow rule run.'
            : 'Off: every toolkit runs unless a rule denies it.'}{' '}
          <Link to="/settings">Change it in Settings</Link>.
        </p>
      </section>
      <RedirectOriginsSection key={workspace.id} workspace={workspace} />
    </>
  )
}

export function WorkspaceView() {
  const { workspaceId } = useSession()
  // Server role checks, not permissions (see the header): cite workspaces.py.
  const canRename = useIsRole('owner', 'admin') // update_workspace: owner or admin only
  const canTransfer = useIsRole('owner') // transfer_ownership: owner only
  const canDelete = useCan('delete_team')
  const [deleting, setDeleting] = useState(false)
  const { effective: developer } = useMode()
  const ws = useQuery({
    queryKey: qk.workspace(workspaceId),
    queryFn: () => apiFetch<Workspace>(`/workspaces/${workspaceId}`),
    enabled: !!workspaceId,
  })
  const workspace = ws.data

  return (
    <div className="page page--narrow">
      <PageHeader title="Workspace" description={workspace ? workspace.name : 'Name, ownership and lifecycle of this workspace.'} />
      <QueryState isLoading={ws.isLoading} error={ws.error} what="this workspace" onRetry={() => ws.refetch()}>
        {workspace && (
          <>
            {canRename ? (
              <RenameSection key={workspace.id} workspace={workspace} />
            ) : (
              <p className="muted" role="note">Only a workspace owner or admin can change these settings.</p>
            )}
            <PlanSection />
            {developer && <DeveloperSettings workspace={workspace} />}
            {canTransfer && <TransferSection />}
            {canDelete && (
              <section className="card-section danger-zone" aria-labelledby="ws-delete-title">
                <h2 id="ws-delete-title" className="section-title">Delete workspace</h2>
                <p className="muted">Removes the workspace for everyone. This can’t be undone from here.</p>
                <div>
                  <button type="button" className="btn btn--danger" onClick={() => setDeleting(true)}>
                    Delete workspace…
                  </button>
                </div>
              </section>
            )}
            {deleting && <DeleteDialog workspace={workspace} onClose={() => setDeleting(false)} />}
          </>
        )}
      </QueryState>
    </div>
  )
}
